import React from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
// Study list variants are selected by the `workList.variant` customization:
// - `'legacy'`  → LegacyWorkList (the pre-3.13 study list)
// - anything else (including `'default'`) → WorkList (ui-next study list)
import WorkList from './WorkList/WorkList';
import LegacyWorkList from './LegacyWorkList/LegacyWorkList';
import DataSourceWrapper from './DataSourceWrapper';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4 data-cy="study-not-found-message">
          One or more of the requested studies are not available at this time.
        </h4>
        {showStudyList && (
          <p
            className="mt-2"
            data-cy="return-to-study-list-message"
          >
            Return to the{' '}
            <Link
              className="text-highlight"
              to="/"
            >
              study list
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// RAIOSS: handles the root route when showStudyList is false.
//
// The standard OHIF root route (WorkListRoute at `/`) only exists when
// `showStudyList` is true. In the Raioss viewer `showStudyList` is false, so a
// direct link like `/?StudyInstanceUIDs=...&datasources=dicomlocal` (which is
// exactly what the `/local` drop page produces) used to fall through to the
// 404 page. This redirect sends it to the correct mode route:
//
//   /?StudyInstanceUIDs=...&datasources=dicomlocal  ->  /viewer/dicomlocal?...
//
// Anything else at `/` — including `/?dataSources=dicomjson`, which carries no
// study to open — is not a viewer entry point and must not hand the user a way
// in. It renders the 404 page rather than redirecting to the `/local` drop
// page, which would otherwise expose local study upload from the bare root.
const RootRedirect = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studyInstanceUIDs = searchParams.getAll('StudyInstanceUIDs');
  const dataSourceName = (searchParams.get('datasources') || '').split(',')[0];

  if (studyInstanceUIDs.length > 0 && dataSourceName) {
    const target = `/viewer/${dataSourceName}${location.search}`;
    return <Navigate to={target} replace />;
  }

  return <NotFound />;
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }), // navigate to the viewer (dicomlocal)
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
  {
    path: `/`,
    children: RootRedirect,
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  const workListVariant = customizationService.getCustomization('workList.variant');
  const WorkListComponent = workListVariant === 'legacy' ? LegacyWorkList : WorkList;

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkListComponent, servicesManager, extensionManager, commandsManager },
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
